/**
 * Client-owned site command admission and reconciliation state.
 *
 * Contains:
 * - siteCommands reducer and authoritative REST/socket replacement action
 * - local begin/promote/end dispatch lifecycle
 * - command/OTA busy selectors and command-log invalidation revision
 * - configuration-write lease runner
 *
 * Returns plain JS — no JSX or network I/O.
 */
import { createSlice, nanoid } from '@reduxjs/toolkit';

import {
  beginCommandActivityRequest,
  COMMAND_ACTIVITY_SOURCE,
  endCommandActivityRequest,
  IDLE_COMMAND_ACTIVITY,
  isSiteCommandLocked,
  promoteCommandActivityRequest,
  replaceCommandActivityEntry,
  resolveVisibleCommandActivity,
} from './activity.js';

const bumpCommandLogRevision = (state, key) => {
  state.commandLogRevisionBySiteId[key] = (state.commandLogRevisionBySiteId[key] ?? 0) + 1;
};

const slice = createSlice({
  name: 'siteCommands',
  initialState: {
    bySiteId: {},
    acceptedDispatchSnapshotRequestVersionBySiteId: {},
    latestAppliedSnapshotRequestVersionBySiteId: {},
    latestSocketSnapshotRequestVersionBySiteId: {},
    commandLogRevisionBySiteId: {},
  },
  reducers: {
    replaceCommandActivity(state, action) {
      const { siteId, activity, generatedAt = null, source, requestVersion = null } = action.payload;
      const key = String(siteId);
      const acceptedDispatchRequestVersion = state.acceptedDispatchSnapshotRequestVersionBySiteId[key];
      const latestAppliedRequestVersion = state.latestAppliedSnapshotRequestVersionBySiteId[key];
      const latestSocketRequestVersion = state.latestSocketSnapshotRequestVersionBySiteId[key];

      if (
        source === COMMAND_ACTIVITY_SOURCE.REST &&
        ((acceptedDispatchRequestVersion != null &&
          (requestVersion == null || requestVersion <= acceptedDispatchRequestVersion)) ||
          (latestSocketRequestVersion != null &&
            (requestVersion == null || requestVersion <= latestSocketRequestVersion)) ||
          (requestVersion != null &&
            latestAppliedRequestVersion != null &&
            requestVersion < latestAppliedRequestVersion))
      ) {
        return;
      }

      state.bySiteId[key] = replaceCommandActivityEntry(state.bySiteId[key], activity, generatedAt);
      bumpCommandLogRevision(state, key);
      if (source === COMMAND_ACTIVITY_SOURCE.REST && requestVersion != null) {
        state.latestAppliedSnapshotRequestVersionBySiteId[key] = requestVersion;
        if (acceptedDispatchRequestVersion != null && requestVersion > acceptedDispatchRequestVersion) {
          delete state.acceptedDispatchSnapshotRequestVersionBySiteId[key];
        }
        if (latestSocketRequestVersion != null && requestVersion > latestSocketRequestVersion) {
          delete state.latestSocketSnapshotRequestVersionBySiteId[key];
        }
      } else if (source === COMMAND_ACTIVITY_SOURCE.SOCKET && requestVersion != null) {
        state.latestSocketSnapshotRequestVersionBySiteId[key] = requestVersion;
      }
    },
    startCommandDispatch(state, action) {
      const { siteId, requestId } = action.payload;
      const key = String(siteId);
      state.bySiteId[key] = beginCommandActivityRequest(state.bySiteId[key], requestId);
    },
    endCommandDispatch(state, action) {
      const { siteId, requestId } = action.payload;
      const key = String(siteId);
      state.bySiteId[key] = endCommandActivityRequest(state.bySiteId[key], requestId);
    },
    promoteCommandDispatch(state, action) {
      const { siteId, requestId, pendingCount = 1, snapshotRequestVersion = 0 } = action.payload;
      const key = String(siteId);
      state.bySiteId[key] = promoteCommandActivityRequest(state.bySiteId[key], requestId, pendingCount);
      state.acceptedDispatchSnapshotRequestVersionBySiteId[key] = snapshotRequestVersion;
      bumpCommandLogRevision(state, key);
    },
  },
});

const { promoteCommandDispatch: promoteCommandDispatchAction, startCommandDispatch } = slice.actions;
export const { endCommandDispatch, replaceCommandActivity } = slice.actions;

/** Hand a successful local write off without accepting a pre-dispatch REST snapshot. */
export const promoteCommandDispatch =
  ({ siteId, requestId, pendingCount = 1 }) =>
  (dispatch, getState) => {
    const snapshotRequestVersion = getState().siteUnit?.latestSnapshotRequestVersionBySiteId?.[String(siteId)] ?? 0;
    dispatch(promoteCommandDispatchAction({ siteId, requestId, pendingCount, snapshotRequestVersion }));
  };

/** Atomically reserve the site's one local command-dispatch slot. */
export const beginCommandDispatch =
  ({ siteId, requestId = nanoid() }) =>
  (dispatch, getState) => {
    if (siteId == null) return null;
    const state = getState();
    const key = String(siteId);
    const entry = state.siteCommands?.bySiteId?.[key];
    const acceptedDispatchPending = state.siteCommands?.acceptedDispatchSnapshotRequestVersionBySiteId?.[key] != null;
    const upgradeStartGuard = state.siteUnit?.upgradeStartPendingBySiteId?.[key];
    const activeUpgrade =
      (upgradeStartGuard != null && upgradeStartGuard.awaitingSnapshot !== false) ||
      state.siteUnit?.bySiteId?.[key]?.current_upgrade != null;
    if (isSiteCommandLocked(entry?.activity, entry?.localRequestId != null || acceptedDispatchPending, activeUpgrade)) {
      return null;
    }
    dispatch(startCommandDispatch({ siteId, requestId }));
    return requestId;
  };

/** @returns {function} selector returning the site's visible command activity */
export const selectSiteCommandActivity = siteId => state => {
  const key = String(siteId);
  const entry = state.siteCommands.bySiteId[key];
  const acceptedDispatchPending = state.siteCommands.acceptedDispatchSnapshotRequestVersionBySiteId?.[key] != null;
  return resolveVisibleCommandActivity(
    entry?.activity,
    entry?.localRequestId ?? (acceptedDispatchPending ? 'accepted' : null),
  );
};

/** @returns {function} selector returning the site's command-log invalidation revision */
export const selectSiteCommandLogRevision = siteId => state =>
  state.siteCommands.commandLogRevisionBySiteId[String(siteId)] ?? 0;

/** @returns {function} selector covering an accepted OTA start and an active upgrade */
export const selectSiteUpgradeBusy = siteId => state => {
  const key = String(siteId);
  const upgradeStartGuard = state.siteUnit?.upgradeStartPendingBySiteId?.[key];
  return (
    (upgradeStartGuard != null && upgradeStartGuard.awaitingSnapshot !== false) ||
    state.siteUnit?.bySiteId?.[key]?.current_upgrade != null
  );
};

/** @returns {function} selector: an accepted OTA start still awaiting its unit snapshot */
export const selectSiteUpgradeStartPending = siteId => state => {
  const upgradeStartGuard = state.siteUnit?.upgradeStartPendingBySiteId?.[String(siteId)];
  return upgradeStartGuard != null && upgradeStartGuard.awaitingSnapshot !== false;
};

/** @returns {function} selector returning whether the site's command launchers are interlocked */
export const selectSiteCommandLocked = siteId => state => {
  const key = String(siteId);
  const entry = state.siteCommands.bySiteId[key];
  const acceptedDispatchPending = state.siteCommands.acceptedDispatchSnapshotRequestVersionBySiteId?.[key] != null;
  const upgradeStartGuard = state.siteUnit?.upgradeStartPendingBySiteId?.[key];
  const activeUpgrade =
    (upgradeStartGuard != null && upgradeStartGuard.awaitingSnapshot !== false) ||
    state.siteUnit?.bySiteId?.[key]?.current_upgrade != null;
  return isSiteCommandLocked(
    entry?.activity ?? IDLE_COMMAND_ACTIVITY,
    entry?.localRequestId != null || acceptedDispatchPending,
    activeUpgrade,
  );
};

/** Execute a write while holding the site's local command-dispatch lease. */
export async function runSiteCommandWrite({ requiresCommand, begin, promote, end, write, pendingCount = 1 }) {
  if (!requiresCommand) {
    return { executed: true, value: await write() };
  }

  const requestId = begin();
  if (requestId == null) return { executed: false, value: undefined };

  try {
    const value = await write();
    promote(requestId, pendingCount);
    return { executed: true, value };
  } finally {
    end(requestId);
  }
}

export default slice.reducer;
